import axios from 'axios';
import generateVersionHash from '@utils/generateVersionHash';
import encryptAES256GCM from '@utils/encryptAES';
import api from './constants';
import mutations from './mutations';
import state from './state';
import { postApi } from '../../lib/fetcher';
import { showAlert } from '../../lib/helpers';


const merchantCallbackManagement = {
  namespaced: true,
  state,
  mutations,
  actions: {
    async getData(ctx, { page, itemsPerPage, search, date }) {
      const URL_API = api.GET_LIST_MERCHANT_CALLBACK;

      if (ctx.state.requestData) ctx.state.requestData.cancel();

      const axiosSource = axios.CancelToken.source();

      ctx.commit('setCancelation', {
        cancel: axiosSource.cancel,
        msg: 'Loading...',
      });

      ctx.commit('clearData');
      ctx.commit('setLoading', true);

      const version = generateVersionHash(page.toString() + itemsPerPage.toString() + date);
      const data = JSON.stringify({
        page: page.toString(),
        limit: itemsPerPage.toString(),
        search,
        version,
      });

      const addConfig = { cancelToken: axiosSource.token };

      const responseData = await postApi(ctx, URL_API, data, addConfig);

      if (responseData?.code === 200) {
        ctx.commit('setTotalData', { payload: responseData });
        ctx.commit('setData', { payload: responseData });
      }
    },
    async getDetail(ctx, { date, id }) {
      const URL_API = api.GET_DETAIL_MERCHANT_CALLBACK;

      ctx.commit('setLoading', true);
      try {
 
        const version = generateVersionHash(id + date);
        const data = JSON.stringify({
          client_id: id,
          version,
        });

        const responseData = await postApi(ctx, URL_API, data);

        if (responseData && responseData.code === 200) {
          ctx.commit('setDataDetail', { payload: responseData });
          return responseData.data || null;
        } 
        return null;
    
      } catch(err) {
        showAlert(ctx, err.response?.data?.data || 'Failed to fetch detail', 'mdi-alert', 'red');
        return null;
      } finally {
        ctx.commit('setLoading', false);
      }
    },
    async getTableDetail(ctx, { page, itemsPerPage, clientID, search, date }) {
      const URL_API = api.GET_DETAIL_TABLE_MERCHANT_CALLBACK;

      if (ctx.state.requestData) ctx.state.requestData.cancel();

      const axiosSource = axios.CancelToken.source();

      ctx.commit('setCancelation', {
        cancel: axiosSource.cancel,
        msg: 'Loading...',
      });

      ctx.commit('clearData');
      ctx.commit('setLoading', true);

      try {
        const version = generateVersionHash(page.toString() + itemsPerPage.toString() + date);
        const data = JSON.stringify({
          page: page.toString(),
          limit: itemsPerPage.toString(),
          search,
          version,
          client_id: clientID
        });

        const addConfig = { cancelToken: axiosSource.token };

        const responseData = await postApi(ctx, URL_API, data, addConfig);

        if (responseData?.code === 200) {
          ctx.commit('setTotalData', { payload: responseData });
          ctx.commit('setData', { payload: responseData });
        }
        return responseData;
      } catch (err) {
        showAlert(ctx, err.response?.data?.data || 'Failed to fetch data', 'mdi-alert', 'red');
        return null;
      } finally {
        ctx.commit('setLoading', false);
      }
    },
    async generateKey(ctx, { date, id }) {
      const URL_API = api.GENERATE_SNAP_OUTBOUND_PAIR_KEY;

      ctx.commit('setLoadingGenerate', true);
      try {
 
        const version = generateVersionHash(id + date);
        const data = JSON.stringify({
          client_id: id,
          version,
        });

        const responseData = await postApi(ctx, URL_API, data);

        if (responseData && responseData.code === 200) {
          ctx.commit('setPairKey', { payload: responseData });
          return responseData.data || null;
        } 
        showAlert(ctx, responseData?.data || 'Generate Failed', 'mdi-alert', 'red');
        
        return null;

      } catch(err) {
        showAlert(ctx, err.response?.data?.data || 'Generate Failed', 'mdi-alert', 'red');
        return null;
      } finally {
        ctx.commit('setLoadingGenerate', false);
      }
    },
    async updateMerchantSnap(ctx, { date, payload, isClientChange }) {
      const URL_API = api.UPDATE_MERCHANT_SNAP;
      const gcmKey = process.env.VUE_APP_GCM_SECRET;
      ctx.commit('setLoading', true);
      try {
 
        const version = generateVersionHash(payload.client_id + date);
        const payloadData = payload.client_type === '1' ? {
          client_id: payload.client_id,
          client_type: payload.client_type,
          callback_url: payload.callback_url,
          version,
        } : {
          client_id: payload.client_id,
          client_type: payload.client_type,
          callback_url: payload.callback_url,
          callback_partner_id: payload.callback_partner_id,
          callback_partner_secret: payload.callback_partner_secret,
          callback_partner_url_token: payload.callback_partner_url_token,
          inbound_merchant_public_key: payload.inbound_merchant_public_key ? encryptAES256GCM(payload.inbound_merchant_public_key, gcmKey) : '',
          outbound_public_key: payload.outbound_public_key ? encryptAES256GCM(payload.outbound_public_key, gcmKey) : '',
          outbound_private_key: payload.outbound_private_key ? encryptAES256GCM(payload.outbound_private_key, gcmKey) : '',
          version,
        };
        
        const data = JSON.stringify(payloadData);

        const responseData = await postApi(ctx, URL_API, data);
        if (responseData?.code === 200) {
          if (!isClientChange) {
            showAlert(ctx, 'Successfully Updated', 'mdi-check', 'green');
          }
          return { success: true };
        } 
        showAlert(ctx, responseData?.message_en || 'Update Failed', 'mdi-alert', 'red');
        return { success: false }; 
    
    
      } catch(err) {
        const errorData = err.response?.data?.data || err?.data;
        let errorMessage = 'Update Failed';
        
        if (typeof errorData === 'string') {
          errorMessage = errorData;
        } else if (typeof errorData === 'object' && errorData !== null && Object.keys(errorData).length > 0) {
          errorMessage = Object.values(errorData).join(', ');
        } else if (err.response?.data?.message_en) {
          errorMessage = err.response.data.message_en;
        }
        
        showAlert(ctx, errorMessage, 'mdi-alert', 'red');
        return { success: false };
      } finally {
        ctx.commit('setLoading', false);
      }
    },
  }
};

export default merchantCallbackManagement;
